'use client';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Radio,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { AbgTypography, mergeSx } from '@abg/kit-base';
import { RadioCheckedIcon } from '@abg/kit-base/lib/components/radio/RadioCheckedIcon';
import { RadioUncheckedIcon } from '@abg/kit-base/lib/components/radio/RadioUncheckedIcon';
import { AbgSxProps } from '@abg/typings';

import { AncillariesPageType } from '../../../shared/constants';
import {
  useAncillariesPageType,
  useTranslationContext,
} from '../../../shared/context';
import { AncillariesI18nKeys } from '../../../shared/types';
import { sanitizeHtml } from '../../../shared/utils';
import { AemImage } from '../../AemImage';

import { CheckmarkIcon } from './icons/CheckmarkIcon';
import {
  checkboxButtonStyling,
  checkboxCheckedIconStyling,
  checkboxIconStyling,
  containerStyling,
  descriptionStyling,
  headerStyling,
  imageContainerStyling,
  radioButtonStyling,
  recommendedStyling,
  titleContainerStyling,
} from './AncillariesBundleHeader.styling';
import { AncillariesBundleRating } from './AncillariesBundleRating';
import { RECOMMENDED_LABEL_TEST_ID } from './constants';
import { AncillariesBundleHeaderProps } from './types';

function getRatingColors(styles: AbgSxProps | undefined): string[] {
  return (styles as Record<string, string[]>)?.['backgroundColor'] ?? [];
}

export const AncillariesBundleHeader = ({
  styleParts = {},
  image,
  title,
  code,
  description,
  rating,
  bookAgain,
  recommended,
  disabled,
  // included,
  selected,
  selectionMode,
}: AncillariesBundleHeaderProps) => {
  const pageType = useAncillariesPageType();
  const { t, i18nKeys } = useTranslationContext<AncillariesI18nKeys>();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const outerCircleSizeSx = {
    width: '24px',
    height: '24px',
  };

  const shouldRenderRecommendedLabel = isDesktop && recommended && !bookAgain;

  return (
    <Box sx={containerStyling} data-testid="ancillaries-bundle-header">
      {!!image?.src && (
        <Box sx={mergeSx(imageContainerStyling, styleParts.bundleImage)}>
          <AemImage
            src={image.src}
            alt={image.alt}
            fill
            style={{ objectFit: 'cover' }}
            data-testid="ancillaries-bundle-image"
          />
        </Box>
      )}

      {shouldRenderRecommendedLabel && (
        <Box
          sx={mergeSx(recommendedStyling, styleParts.recommended)}
          data-testid={RECOMMENDED_LABEL_TEST_ID}
        >
          {t(i18nKeys.recommendedLabel)}
        </Box>
      )}

      <Box sx={mergeSx(styleParts.bundleHeader, headerStyling(pageType))}>
        <FormControlLabel
          sx={titleContainerStyling(pageType)}
          labelPlacement="start"
          label={
            <AbgTypography
              sx={{ ...styleParts.bundleTitle }}
              className="ancillaries-bundle__title"
              data-testid="ancillaries-bundle-title"
            >
              {title}
            </AbgTypography>
          }
          control={
            disabled ? (
              <AbgTypography variant="bodySmallRegular">
                {t(i18nKeys['reservationProtections.bundles.notAvailable'])}
              </AbgTypography>
            ) : selectionMode === 'checkbox' ? (
              <Checkbox
                sx={checkboxButtonStyling}
                value={code}
                checked={Boolean(selected)}
                disableRipple
                slotProps={{ input: { 'aria-label': title } }}
                icon={<Box sx={checkboxIconStyling} />}
                checkedIcon={
                  <Box sx={checkboxCheckedIconStyling}>
                    <CheckmarkIcon />
                  </Box>
                }
                data-testid="ancillaries-bundle-checkbox-btn"
              />
            ) : (
              <Radio
                sx={radioButtonStyling}
                value={code}
                slotProps={{ input: { 'aria-label': title } }}
                icon={
                  <RadioUncheckedIcon
                    sxOverrides={(theme) => ({
                      ...outerCircleSizeSx,
                      border: `1px solid ${theme.palette.secondary.dark}`,
                    })}
                  />
                }
                checkedIcon={
                  <RadioCheckedIcon
                    sxOverrides={{
                      innerCircleSx: (theme) => ({
                        backgroundColor: `${theme.palette.secondary.dark}`,
                        width: '16px',
                        height: '16px',
                      }),
                      outerCircleSx: (theme) => ({
                        ...outerCircleSizeSx,
                        border: `1px solid ${theme.palette.secondary.dark}`,
                      }),
                    }}
                  />
                }
                data-testid="ancillaries-bundle-radio-btn"
              />
            )
          }
        />

        {rating != null && (
          <AncillariesBundleRating
            styleParts={styleParts}
            rating={rating}
            ratingColors={getRatingColors(styleParts.coverageRating)}
          />
        )}

        <AbgTypography
          sx={mergeSx(descriptionStyling, styleParts.description)}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
          data-testid="ancillaries-bundle-description"
        />

        {pageType === AncillariesPageType.AddOns && <Divider />}
      </Box>
    </Box>
  );
};
